import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { Checkbox } from "../../../UI.BaseControls/Checkbox";
import { Edit } from "../../../UI.BaseControls/Edit";
import { EventHelper } from "../../../UI.BaseControls/Helpers/EventHelper";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { FormProfileManager } from "../XGControls/Forms/FormProfileManager";
import { IContentBuilder } from "./IContentBuilder";

/** Detailový prvek pro editaci profilu */
export class FormProfileDetailBuilder implements IContentBuilder {
    //#region Fields
    private readonly _element: HTMLElement;
    private readonly _nameEdit: Edit;
    private _profileManager: FormProfileManager;
    //#endregion Fields

    /**
     * Vytvoří detail pro editaci profilu
     * @param params Nastavení profilu
     */
    constructor(params: IProfileInputData) {
        let name: string = params.name;
        let queryMode: boolean = params.queryMode ?? false;
        let showInNavigationTree: boolean = params.showInNavigationTree;
        let isDefault: boolean = params.isDefault;
        let storeFilters: boolean = params.storeFilters ?? true;
        let isImplementation: boolean = params.isImplementation;
        const nameLoadPromise: Promise<void> = Promise.resolve();

        this._profileManager = params.profileManager;

        this._element = createElement({ classes: "edit" });

        const headerContainer = createElement({ classes: "header-container" });

        this._nameEdit = new Edit();
        this._nameEdit.value = name ?? "";
        this._nameEdit.addEventListener("input", () => name = this._nameEdit.value);
        this._nameEdit.addEventListener("keypress", (e: KeyboardEvent) => {
            if (e.key === "Enter") {
                saveFunction();
            }
        });
        this._nameEdit.afterAttach(item => item.focus());

        Localization.addLocDescData(this._nameEdit, "GUI-669057", ["title", "placeholder"]); // Název zobrazení
        headerContainer.appendChild(this._nameEdit);

        const saveMenuButtons = createElement({ classes: "buttons" });

        const saveButton = createElement({ type: "button", classes: "SaveButton flat icon tool-floppy-disk btn no-caption" });
        Localization.addLocData(saveButton, "GUI-624405", "title", ""); // Uložit zobrazení

        const saveFunction = async () => {
            await nameLoadPromise;
            params.saveFunction({
                isDefault: isDefault,
                name: name,
                queryMode: queryMode,
                showInNavigationTree: showInNavigationTree,
                storeFilters: storeFilters,
                isImplementation: isImplementation,
            });
        };

        saveButton.addEventListener("click", (e: MouseEvent) => {
            if (saveButton.classList.contains("Disabled"))
                return;
            if (e.button === 0) { // Levé tlačítko myši
                EventHelper.stopImmediatePropagation(e);
                saveFunction();
            }
        });

        saveMenuButtons.appendChild(FormProfileDetailBuilder.createCancelButton(params.cancelFunction));
        saveMenuButtons.appendChild(saveButton);

        headerContainer.appendChild(saveMenuButtons);
        this._element.appendChild(headerContainer);

        const checkBoxesElement = createElement({ classes: "options" });

        if (this._profileManager.canEditImplementationProfiles) {
            const checkBoxImp = this._createCheckBoxWithCaption("GUI-675712"); // Implementační
            checkBoxImp.addEventListener("change", () => isImplementation = checkBoxImp.checked || false);
            checkBoxImp.checked = isImplementation;
            checkBoxesElement.appendChild(checkBoxImp);
        }

        const checkBoxDefault = this._createCheckBoxWithCaption("GUI-624532"); // Výchozí
        checkBoxDefault.addEventListener("change", () => isDefault = checkBoxDefault.checked || false);
        checkBoxDefault.checked = isDefault;
        checkBoxesElement.appendChild(checkBoxDefault);

        if (params.isBasicProfile) {
            this._nameEdit.enabled = false;
        } else {
            const checkBoxQueryMode = this._createCheckBoxWithCaption("GUI-677765"); // Načíst data při otevření formuláře
            checkBoxQueryMode.addEventListener("change", () => queryMode = checkBoxQueryMode.indeterminate ? null : !checkBoxQueryMode.checked);
            checkBoxQueryMode.value = !queryMode;
            checkBoxesElement.appendChild(checkBoxQueryMode);

            const checkBoxStoreFilters = this._createCheckBoxWithCaption("GUI-740486"); // Uložit s aktuálními filtry ; Určuje, zda se zobrazení má uložit i s aktuálně použitými filtry
            checkBoxStoreFilters.addEventListener("change", () => storeFilters = checkBoxStoreFilters.checked ?? true);
            checkBoxStoreFilters.value = storeFilters;
            checkBoxesElement.appendChild(checkBoxStoreFilters);

            if (params.showMenuActions) {
                const checkBoxShowInMenu = this._createCheckBoxWithCaption("GUI-506029"); // Zobrazit v menu
                checkBoxShowInMenu.addEventListener("change", () => showInNavigationTree = checkBoxShowInMenu.checked || false);
                checkBoxShowInMenu.checked = showInNavigationTree;
                checkBoxesElement.appendChild(checkBoxShowInMenu);
            }
        }

        this._element.appendChild(checkBoxesElement);
    }

    //#region Methods

    /** Vrátí element profilu */
    public async build(): Promise<HTMLElement> {
        return this._element;
    }

    /**
     * Vytváří CheckBox pro použití v editaci profilu
     * @param locKey Lokalizační klíč popisku
     * @param threeState Zda se jedná o třístavový CheckBox
     */
    private _createCheckBoxWithCaption(locKey: string, threeState = false): Checkbox {
        const checkBox = new Checkbox();
        checkBox.standardAction = !threeState;
        checkBox.isNullable = threeState;

        Localization.addLocData(checkBox, locKey, "text");

        return checkBox;
    }

    /**
     * Vytvoří a vrátí tlačítko pro zrušení editace
     * @param cancel Funkce pro zrušení změn
     */
    public static createCancelButton(cancel: () => void): HTMLElement {
        const cancelButton = createElement({ type: "button", classes: "CancelButton flat icon ui-undo-a btn no-caption" });
        Localization.addLocData(cancelButton, "GUI-7", "title", ""); // Zrušit

        cancelButton.addEventListener("click", (e: MouseEvent) => {
            if (cancelButton.classList.contains("Disabled"))
                return;
            if (e.button === 0) { // Levé tlačítko myši
                EventHelper.stopImmediatePropagation(e);
                cancel();
            }
        });

        return cancelButton;
    }
    //#endregion Methods
}

/** Nastavení profilu */
export interface IProfileData {
    /**
     * Název profilu
     */
    name: string;
    /**
     * Příznak, zda je profil nastaven jako výchozí
     */
    isDefault: boolean;
    /** Zda je implementační (bez vazby na uživatele) */
    isImplementation: boolean;
    /**
     * Příznak, zda se při otevření formu mají načíst data
     */
    queryMode: boolean | null;
    /**
     * Příznak, zda má být profil zobrazen v menu
     */
    showInNavigationTree: boolean;
    /**
     * Příznak, zda se má profil uložit včetně aktivních filtrů na framech
     */
    storeFilters: boolean;
}

/** Nastavení profilu včetně akcí a stavů komponent */
export interface IProfileInputData extends IProfileData {
    /** Profil manager, ke kterému profil patří */
    profileManager: FormProfileManager;
    /** Zda zobrazovat menu akce */
    showMenuActions: boolean;
    /** Zda se jedná o základní profil */
    isBasicProfile: boolean;
    /** Metoda pro uložení */
    saveFunction: (result: IProfileData) => Promise<void>;
    /** Metoda pro zrušení změn */
    cancelFunction: () => void;
}
